import React, { useState, useContext, useEffect } from 'react';
import { useStaticQuery, graphql, Link  } from 'gatsby';
import { Helmet } from 'react-helmet';
import { transformImageUrl } from './SafeSiteImage';
import styled, { css, useTheme } from 'styled-components';
import ChevronDownIcon from './Icons/ChevronDown';
import BasketIcon from './Basket';
import { QuoteBasketContext } from '../context/QuoteBasketContext';
import MegaMenu from './MegaMenu';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { convertToRelativePath } from '../utils/convertToRelativePath';
import { globalHistory } from "@reach/router";

const NavigationStyles = styled.nav`
  ${({ theme, $visible }) => css`
    width: 100%;
    position: relative;
    z-index: 0;
    > ul {
      display: flex;
      width: 100%;
      justify-content: flex-end;
      gap: 8px;
      > li {
        display: flex;
        flex-flow: column;
        justify-content: center;
      }
    }
    @media (max-width: 991px) {
      display: ${$visible ? 'block' : 'none'};
      position: fixed;
      top: 68px;
      left: 0;
      width: 100vw;
      height: calc(100vh - 68px);
      overflow: auto;
      background: #fff;
      z-index: 9999;
      padding: 16px;
      border-top: solid 1px ${theme.colors.lightGrey};
      > ul {
        gap: 0.5rem;
        flex-flow: column;
        > li {
          padding-bottom: 0.5rem;
          border-bottom: 1px solid ${theme.colors.darkGrey};

          &:has(button[aria-label='Basket']) {
            display: none;
          }
        }
      }
    }
  `}
`;

const NavItemStyles = styled.li`
  ${({ theme, $active }) => css`
    > button,
    > a {
      all: revert;
      border: 0;
      background: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      font-weight: 900;
      letter-spacing: 0.5px;
      gap: 0.5rem;
      color: ${theme.colors.darkGrey};
      text-decoration: none;
      text-transform: uppercase;
      font-family: ${theme.fontFamily};

      > svg {
        color: ${theme.colors.primary};
        width: 1.25rem;
        height: 1.25rem;
      }

      &:hover {
        background: none;
        color: ${theme.colors.primary};
      }
    }

    @media (max-width: 991px) {
      > button,
      > a {
        justify-content: space-between;
        line-height: 48px;
        font-size: 0.75rem;
        width: 100%;
        padding: 0 16px;
        > svg {
          display: ${$active ? 'none' : 'block'};
          transform: rotate(-90deg);
        }
        &::after {
          display: ${$active ? 'block' : 'none'};
          content: '-';
          color: ${theme.colors.primary};
          font-weight: 200;
          font-size: 3rem;
          margin-right: -4px;
        }
      }
    }
    @media (min-width: ${theme.breakpoints.lg}) {
      > button,
      > a {
        padding: 16px 8px;
        font-size: 1.1rem;
        color: ${$active ? theme.colors.primary : 'inherit'};

        > svg {
          transform: ${$active ? 'rotate(180deg)' : 'none'};
        }
      }
    }
  `}
`;

const BasketButtonStyles = styled.button`
  ${({ theme }) => css`
    background: none;
    padding: 0;
    line-height: 12px;
    color: ${theme.colors.darkGrey};
    padding: 12px 0;

    svg {
      flex-shrink: 0;
      height: 24px;
    }

    &:hover,
    &:focus {
      background: #fff;
      outline: none;
      color: ${theme.colors.primary};
    }
  `}
`;

export const Navigation = ({
  handleOpenNav,
  handleCloseNav,
  visible,
  menuItems: itemsProp,
  basketEnabled,
}) => {
  const { setQuoteBasketIsActive } = useContext(QuoteBasketContext);

  const data = useStaticQuery(graphql`
    query ProductMenuQuery {
      site {
        siteMetadata {
          siteUrl
        }
      }
      wpMenu(name: { eq: "Header Product Navigation" }) {
        menuItems {
          nodes {
            key: id
            parentId
            title: label
            path
            menuItemAcf {
              icon {
                mediaItemUrl
              }
              promoImages {
                heading
                image {
                  mediaItemUrl
                }
                link {
                  url
                  target
                  title
                }
              }
              image {
                mediaItemUrl
              }
              description
            }
            childItems {
              nodes {
                key: id
                parentId
                title: label
                path
              }
            }
          }
        }
      }
      wp {
        siteSettings {
          siteSettings {
            enableQuoteBasketSiteWide
          }
        }
      }
    }
  `);

  const flatListToHierarchical = (
    data = [],
    {
      idKey = 'key',
      parentKey = 'parentId',
      childrenKey = 'children',
    } = {},
  ) => {
    const tree = [];
    const childrenOf = {};
    data.forEach((item) => {
      const newItem = { ...item };
      const { [idKey]: id, [parentKey]: parentId = 0 } = newItem;
      childrenOf[id] = childrenOf[id] || [];
      newItem[childrenKey] = childrenOf[id];
      parentId
        ? (childrenOf[parentId] = childrenOf[parentId] || []).push(
            newItem,
          )
        : tree.push(newItem);
    });
    return tree;
  };

  const menuItemsFromQuery = data?.wpMenu?.menuItems?.nodes
    ? flatListToHierarchical(data.wpMenu.menuItems.nodes)
    : [];
  const menuItems = React.useMemo(() => {
    return itemsProp || menuItemsFromQuery;
  }, [itemsProp, menuItemsFromQuery]);

  const imageUrls = React.useMemo(() => {
    const urls = new Set();
    const collect = (item) => {
      if (item.menuItemAcf?.icon?.mediaItemUrl) {
        urls.add(
          transformImageUrl({
            siteUrl: data.site.siteMetadata.siteUrl,
            imageUrl: item.menuItemAcf.icon.mediaItemUrl,
            width: 27,
            height: 27,
          }),
        );
      }
      if (item.menuItemAcf?.image?.mediaItemUrl) {
        urls.add(
          transformImageUrl({
            siteUrl: data.site.siteMetadata.siteUrl,
            imageUrl: item.menuItemAcf.image.mediaItemUrl,
          }),
        );
      }
      if (item.menuItemAcf?.promoImages?.length) {
        item.menuItemAcf.promoImages.forEach((img) => {
          if (img?.image?.mediaItemUrl) {
            urls.add(
              transformImageUrl({
                siteUrl: data.site.siteMetadata.siteUrl,
                imageUrl: img.image.mediaItemUrl,
                width: 300,
              }),
            );
          }
        });
      }
      if (item.children?.length) {
        item.children.forEach(collect);
      }
    };
    menuItems.forEach(collect);

    return Array.from(urls);
  }, [menuItems, data.site.siteMetadata.siteUrl]);

  const basketIsEnabled =
    typeof basketEnabled !== 'undefined'
      ? basketEnabled
      : data.wp.siteSettings.siteSettings.enableQuoteBasketSiteWide;

  const theme = useTheme();
  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints.lg})`,
  );

  const [activeIndex, setActiveIndex] = useState(isMobile ? 0 : null);
  const [currentPath, setCurrentPath] = useState(() =>
    convertToRelativePath(globalHistory.location?.pathname || ''),
  );

  useEffect(() => {
    return globalHistory.listen(({ location }) => {
      setCurrentPath(convertToRelativePath(location.pathname));
    });
  }, []);

  useEffect(() => {
    if (!isMobile || menuItems.length === 0) {
      return
    }

    const index = menuItems.findIndex((item) => {
      const primaryPath = convertToRelativePath(item.path);
      if (currentPath === primaryPath || currentPath.startsWith(`${primaryPath}/`)) {
        return true;
      }

      return item.children?.some((child) => {
        const secPath = convertToRelativePath(child.path);

        if (currentPath === secPath || currentPath.startsWith(`${secPath}/`)) {
          return true;
        }

        return child.children?.some((ter) => {
          const terPath = convertToRelativePath(ter.path);

          return currentPath === terPath || currentPath.startsWith(`${terPath}/`);
        });
      });
    });

    setActiveIndex(index === -1 ? 0 : index);
  }, [currentPath, isMobile]);

  const handleToggle = (index, e) => {
    e.preventDefault();
    setActiveIndex(activeIndex === index ? null : index);
    if (!isMobile) {
      if (activeIndex === index) {
        handleCloseNav();
      } else {
        handleOpenNav();
      }
    }
  };

  useEffect(() => {
    if (!visible) {
      setActiveIndex(null)
    }
  }, [visible])

  const handleClose = () => {
    setActiveIndex(null);
    !isMobile ? handleCloseNav() : null;
  }

  const handleOpenQuoteBasket = () => {
    handleClose();
    setQuoteBasketIsActive(true);
  };

  return (
    <>
      <Helmet>
        {imageUrls.map((src) => (
          <link key={src} rel="preload" as="image" href={src} />
        ))}
      </Helmet>
      <NavigationStyles $visible={visible}>
        <ul>
          {menuItems
            .filter((item) => item.title)
            .map((item, index) => {
              const isActive = activeIndex === index;
              return (
                <NavItemStyles
                  key={item.key}
                  $active={isActive}
                  $hasMegaMenu={item.children.length > 0}
                >
                  {item.children.length > 0 ? (
                    <>
                      <button
                        id={`mega-menu-button-${item.key}`}
                        onClick={(e) => handleToggle(index, e)}
                        aria-haspopup="true"
                        aria-controls={`mega-menu-${item.key}`}
                        aria-expanded={isActive}
                      >
                        {item.title}
                        <ChevronDownIcon />
                      </button>
                      {isActive && (
                        <MegaMenu item={item} onClose={handleClose} />
                      )}
                    </>
                  ) : (
                    <Link to={item.path}>{item.title}</Link>
                  )}
                </NavItemStyles>
              );
            })}
          {basketIsEnabled && (
            <li>
              <BasketButtonStyles
                onClick={handleOpenQuoteBasket}
                aria-label="Basket"
              >
                <BasketIcon />
              </BasketButtonStyles>
            </li>
          )}
        </ul>
      </NavigationStyles>
    </>
  );
};
